import { Button } from "@instacover/ui"
import { Await, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo } from "react"
import { Trans } from "react-i18next"
import { z } from "zod"

import { CameraButton } from "@/components/base/CameraButton"
import ImageWrapper from "@/components/base/ImageWrapper"
import RetakeButton from "@/components/base/RetakeButton"
import ImagesRowWithDetails from "@/components/ImagesRowWithDetails"
import Map, { GeoJsonObj } from "@/components/Map"
import MapSkeleton from "@/components/MapSkeleton"
import { Spinner } from "@/components/Spinner"
import { WallGraphic } from "@/components/wall/WallGraphic"
import { db } from "@/database/database"
import { useOrderedSteps, usePhotosByWall } from "@/database/hooks"
import { wallPathParams } from "@/helpers/zodSchemes"
import { getMarkedWallsOptions } from "@/hooks/api/useGetMarkedWalls"
import useAddWallPhoto from "@/hooks/useAddWallPhoto"
import { useStepsRouting } from "@/hooks/useStepsRouting"
import { BaseLayout } from "@/sections/layouts/BaseLayout"
import PhotoDetailLayout from "@/sections/layouts/PhotoDetailLayout"
import { useModelPredict } from "@/services/model/model"
import { queryClient } from "@/services/queryClient"
import { gpsPerWallPhoto, pickGps } from "@/utils/gps"

import WallCaptured from "./-WallCaptured"

// Builds the map layers. `gps` are the viewpoints the wall photos were taken from — the endpoint
// returns the building polygon, a marker per viewpoint and the walls visible from them.
// Empty gps arr will display just walls without markers.
async function getGeoJsons(sessionId: string, gps: [number, number][]) {
  const markedWalls = await queryClient.ensureQueryData(getMarkedWallsOptions(sessionId, gps))
  const geoJsons: GeoJsonObj[] = []

  if (markedWalls.buildingPolygonGeoJson) {
    geoJsons.push({ type: "BuildingPolygon", geoJson: JSON.parse(markedWalls.buildingPolygonGeoJson) })
  }
  if (markedWalls.photoCoordinatesGeoJson) {
    geoJsons.push({ type: "PhotoCoordinates", geoJson: JSON.parse(markedWalls.photoCoordinatesGeoJson) })
  }
  if (markedWalls.visibleWallsGeoJson) {
    geoJsons.push({ type: "VisibleWalls", geoJson: JSON.parse(markedWalls.visibleWallsGeoJson) })
  }

  return geoJsons
}

const wallSearchParamsSchema = z.object({
  /**
   * This parameter determines whether the map should be hidden when navigating back from the wall detail page.
   */
  retakePhoto: z.boolean().optional(),
})

export const Route = createFileRoute("/$sessionId/_session/_steps/wall/$wallNumber/$wallId")({
  component: WallPhotoRoute,
  validateSearch: (search) => wallSearchParamsSchema.parse(search),
  params: {
    parse: wallPathParams.parse,
  },
  loader: async ({ params }) => {
    const { sessionId } = params

    const wallPhotos = await db.photo.findByType({
      sessionId,
      type: "Wall",
    })

    // Map is shown only if every wall photo has GPS, otherwise show graphic
    const hasEveryWallPhotoGps = wallPhotos.every((photo) => pickGps(photo))

    // do NOT await -> deffered data loading https://tanstack.com/router/v1/docs/framework/react/guide/deferred-data-loading
    const geoJsons = hasEveryWallPhotoGps ? getGeoJsons(sessionId, gpsPerWallPhoto(wallPhotos)) : undefined

    const session = await db.session.get(sessionId)

    return {
      geoJsons,
      session,
    }
  },
})

function WallPhotoRoute() {
  const { wallId, sessionId, wallNumber } = Route.useParams()
  const { retakePhoto } = Route.useSearch()
  const { geoJsons, session } = Route.useLoaderData()
  const navigate = useNavigate()

  const { onRetakePhoto, onUsePhoto, files, select, isUploadingPhoto, photoError } = useAddWallPhoto({
    sessionId,
    wallId,
    wallNumber,
  })

  const file = files?.at(0)
  const { data: modelResult } = useModelPredict(file)

  const { data: photosByWall } = usePhotosByWall({
    sessionId,
    wallNumber: Number(wallNumber),
  })

  const wallPhoto = useMemo(() => photosByWall?.[0], [photosByWall])
  const isFirstWall = useMemo(() => wallNumber === "1", [wallNumber])

  const { firstUnfinishedStepLink } = useStepsRouting(sessionId)

  const onSkipWallStep = useCallback(async () => {
    await db.orderedStep.update({ name: "Wall", instahomeSessionId: sessionId, data: { isFinished: true } })
  }, [sessionId])

  const { data: orderedSteps } = useOrderedSteps(sessionId)
  const wallStep = useMemo(() => orderedSteps?.find((step) => step.name === "Wall"), [orderedSteps])

  // Usually only first 2 walls are mandatory
  const isOptionalWall = useMemo(() => Number(wallNumber) > (wallStep?.minMedia ?? 2), [wallNumber, wallStep?.minMedia])

  useEffect(() => {
    if (!wallStep || !firstUnfinishedStepLink) return

    if (wallStep.isFinished) {
      navigate({ ...firstUnfinishedStepLink, replace: true })
    }
  }, [navigate, firstUnfinishedStepLink, wallStep])

  /* Error state */
  if (photoError) throw photoError

  /* Photo just taken, not confirmed for use */
  if (file) {
    if (!session || !modelResult) return <Spinner size="xl" color="neutral" position="center" />

    return (
      <PhotoJustTaken
        file={file}
        wallNumber={wallNumber}
        wallId={wallId}
        onUsePhoto={() => onUsePhoto({ file, numberOfWalls: session.numberOfWalls, modelResult })}
        onRetake={onRetakePhoto}
        isUploadingPhoto={isUploadingPhoto}
      />
    )
  }

  /* Photo confirmed for use - display map */
  if (!!wallPhoto && !retakePhoto) {
    return (
      <WallCaptured
        photo={wallPhoto}
        sessionId={sessionId}
        wallId={wallId}
        wallNumber={wallNumber}
        onRetake={onRetakePhoto}
      />
    )
  }

  /* No photo yet */
  return (
    <BaseLayout
      aboveHeading={
        geoJsons ? (
          // Previous wall photos have GPS, show map with markers
          <Await promise={geoJsons} fallback={<MapSkeleton />}>
            {(data) => <Map sessionId={sessionId} geoJsons={data} />}
          </Await>
        ) : (
          <WallGraphic type="HouseAround" />
        )
      }
      heading={isFirstWall ? { i18nKey: "wall.heading" } : { i18nKey: "wall.headingAnotherWall" }}
      subheading={{
        i18nKey: "general.howTo",
      }}
      description={
        isFirstWall
          ? {
              i18nKey: "wall.description",
              components: {
                span: <span className="text-ic-primary font-bold uppercase" />,
              },
            }
          : {
              i18nKey: "wall.descriptionAnotherWall",
              components: {
                span: <span className="text-ic-primary font-bold uppercase" />,
              },
            }
      }
      buttons={
        <div className="text-center">
          {/* TODO updatnut pocet stien po nahrati fotky */}
          {isOptionalWall && (
            <div className="flex gap-4">
              <Button type="button" variant="secondary" onClick={onSkipWallStep}>
                <Trans i18nKey="general.skip" />
              </Button>

              <CameraButton onClick={select} />
            </div>
          )}

          {!isOptionalWall && <CameraButton onClick={select} />}
        </div>
      }
    />
  )
}

type PhotoJustTakenProps = {
  file: File
  wallNumber: string
  wallId: string
  isUploadingPhoto: boolean
  onUsePhoto: () => void
  onRetake?: () => void
}

function PhotoJustTaken(props: PhotoJustTakenProps) {
  const { file, wallNumber, onUsePhoto, onRetake, wallId, isUploadingPhoto } = props

  return (
    <PhotoDetailLayout
      aboveHeading={
        <div>
          <ImagesRowWithDetails photo={{ id: wallId, details: [], file }} activePhotoId={wallId} />

          <ImageWrapper photo={{ file: file, id: wallId }} alt="Just taken photo" />
        </div>
      }
      heading={{
        i18nKey: "wall.photoTaken.heading",
        values: { wallNumber },
      }}
      description={{
        i18nKey: "wall.photoTaken.description",
        components: {
          span: <span className="text-ic-primary font-bold uppercase" />,
        },
      }}
      buttons={
        <div className="mb-4 flex gap-4">
          <RetakeButton onClick={onRetake} />

          <Button
            loading={isUploadingPhoto}
            type="button"
            variant="primary"
            as="button"
            onClick={onUsePhoto}
            data-testid="wall-photo-use-btn"
          >
            <Trans i18nKey="general.use" />
          </Button>
        </div>
      }
    />
  )
}
